import React from 'react';
import { Search, ArrowRight, Plane } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Unified "Search Flights" CTA banner shown at the top of every SEO entity /
 * hub page. Replaces the per-page embedded HomePage search widget — a single
 * call-to-action that hands the visitor back to the rich homepage form.
 *
 * Props
 * ─────
 *   prefill   { from, to }   When supplied, the deep-link query string
 *                            prefills the homepage origin/destination via
 *                            ?from=…&to=… (consumed by HomePage initial state).
 *   variant   'default' | 'compact'
 *                            'compact' is used inside narrow content slots
 *                            (e.g. above breadcrumbs on visa pages).
 */
const SeoSearchCTA = ({ prefill, variant = 'default' }) => {
  const params = new URLSearchParams();
  if (prefill?.from) params.set('from', prefill.from);
  if (prefill?.to) params.set('to', prefill.to);
  const qs = params.toString();
  const href = qs ? `/?${qs}#search` : '/#search';

  if (variant === 'compact') {
    return (
      <div className="seo-search-cta seo-search-cta-compact" data-testid="seo-search-cta">
        <Link
          to={href}
          data-testid="seo-search-cta-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1B375B] hover:bg-[#13294B] text-white text-sm font-semibold shadow-sm transition-colors"
        >
          <Search className="w-4 h-4" />
          Search flights
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <section
      className="seo-search-cta"
      data-testid="seo-search-cta"
      aria-label="Search flights on ACF Tourism"
    >
      <div className="seo-search-cta-inner">
        <div className="seo-search-cta-icon" aria-hidden="true">
          <Plane className="w-5 h-5" />
        </div>
        <div className="seo-search-cta-text">
          <p className="seo-search-cta-title">Ready to book?</p>
          <p className="seo-search-cta-sub">
            Compare live fares across 100+ airlines on our home search.
          </p>
        </div>
        <Link
          to={href}
          data-testid="seo-search-cta-link"
          className="seo-search-cta-btn"
        >
          <Search className="w-4 h-4" />
          <span>Search flights</span>
          <ArrowRight className="w-4 h-4 seo-search-cta-arrow" />
        </Link>
      </div>
    </section>
  );
};

export default SeoSearchCTA;
